import { Injectable } from "@angular/core";
import { collection, collectionSnapshots, doc, Firestore } from "@angular/fire/firestore";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { ModalController } from "@ionic/angular";
import { BehaviorSubject, map, Observable, of, shareReplay, switchMap } from "rxjs";
import { ListOfChecksComponent } from "src/app/pages/syndicate/list-of-checks/list-of-checks.component";
import { SyndicateRepositoryService } from "src/app/pages/syndicate/syndicate-repository.service";
import { SyndicateTicketsComponent } from "src/app/pages/syndicate/syndicate-tickets/syndicate-tickets.component";
import { PrivateSyndicateTicketComponent } from "src/app/pages/syndicate/tickets/private-syndicate-ticket/private-syndicate-ticket.component";
import { CustomModalTransitionService } from "../custom-modal-transition.service";
import { GTMSegmentService } from "../gtm-segment.service";
import { SyndicateDataService } from "../syndicate-data.service";

@Injectable({
  providedIn: "root",
})
export class SyndicateRootService {
  backgroundNoScroll = true;
  showPreviousCheckAnimation = new BehaviorSubject<boolean>(false);
  showPreviousCheckAnimation$ = this.showPreviousCheckAnimation.asObservable();

  constructor(
    private firestore: Firestore,
    private modalCtrl: ModalController,
    private cas: CustomModalTransitionService,
    private syndicateDataService: SyndicateDataService,
    private router: Router,
    private syndicateService: SyndicateRepositoryService,
    private bottomSheet: MatBottomSheet,
    private catchService: CatchErrorService,
    private GTMSegment: GTMSegmentService,
    private authService: AuthService,
  ) { }

  async openTickets(syndicate: Data, clickAction: string) {
    this.GTMSegment.HotJarEvent("user_check_tickets");
    if (clickAction !== "") {
      syndicate["click_action"] = clickAction;
    }

    try {
      const syndicateTickets = await this.modalCtrl.create({
        component: SyndicateTicketsComponent,
        componentProps: { syndicate, source: "syndicateTab" },
        cssClass: "modal-fullScreen",
        enterAnimation: this.cas.enterFromRight,
        leaveAnimation: this.cas.exitThroughRight,
      });
      await syndicateTickets?.present();
      syndicateTickets.onDidDismiss()
        .then((res) => {
          if (res?.data && res?.data?.viewMember) {
            if (!syndicate || !syndicate?.id) {
              return;
            }
            this.syndicateDataService?.setSyndicateID(syndicate?.id);
            this.router.navigate([`/private-syndicate/syndicate-member/${syndicate?.id}`]);
          }
        })
        .catch((error) => {
          return error;
        });
    } catch (err) {
      return err;
    }
  }


  // This method is used to get private syndicate ticket
  async getPrivateSyndicate(syndicate, prdIndex) {
    this.syndicateService?.getPrivateSyndicateProducts(syndicate?.sId)?.subscribe(
      async (product) => {
        const syndicateTickets = await this.modalCtrl.create({
          component: PrivateSyndicateTicketComponent,
          componentProps: { syndicate, product, prdIndex, source: "syndicateTab" },
          cssClass: "modal-fullScreen",
          enterAnimation: this.cas.enterFromRight,
          leaveAnimation: this.cas.exitThroughRight,
        });
        syndicateTickets
          .onDidDismiss()
          .then((res) => {
            if (res?.data && res?.data?.viewMember) {
              if (!syndicate || !syndicate?.id) return;
              this.syndicateDataService?.setSyndicateID(syndicate?.id);
              this.router.navigate(["/private-syndicate/syndicate-member/" + syndicate?.id]);
            }
          })
          .catch((error) => {
            return error;
          });
        return await syndicateTickets?.present();
      },
      (err) => {
        return err;
      }
    );
  }

  // This method is to display list of checks bottom sheet
  openListOfChecks(previousChecks: Data) {
    this.backgroundNoScroll = false;
    try {
      const showListOfChecks = this.bottomSheet.open(ListOfChecksComponent, {
        panelClass: previousChecks?.length ? "list-of-check-bottomsheet" : "list-of-checks-empty",
        data: { previousChecks },
      });
      try {
        showListOfChecks.afterDismissed()?.subscribe(
          (responseData) => {
            this.backgroundNoScroll = true;
            if (responseData && responseData?.claimEventId) {
              if (!responseData?.claimEventId) return new Error("ClaimEventId is missing");
              this.router.navigateByUrl(`checkWinnings/breakdown/${responseData?.claimEventId}`);
            }
          },
          (err) => {
            return err;
          }
        );
      } catch (e) {
        return e;
      }

      if (this.showPreviousCheckAnimation.getValue()) {
        this.syndicateService?.updatePreviousCheckNodes("previous_check_animation");
        this.showPreviousCheckAnimation.next(false);
      }
    } catch (e) {
      return e;
    }
  }

  // This function is used to get all managed tickets of the user
  getSyndicateProduct(id: Data) {
    if (!id) return of(null); // Return an observable with null for invalid input
    try {
      return this.catchService.isOnline$.pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of(null); // Handle offline scenario gracefully
          return this.authService.getUid$("SyndicateRootService").pipe(
          switchMap((userId) => {
            if (!userId) userId = null;
            const managedTicketsCollection = collection(
              doc(this.firestore, `/users/${userId}`), // Firestore reference to user document
              'managed_tickets' // Reference to 'managed_tickets' subcollection
            );

            const productsCollection = collection(
              doc(managedTicketsCollection, String(id)), // Reference to specific ticket document
              'products' // Reference to 'products' subcollection
            );

            return collectionSnapshots(productsCollection).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          }));
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      ).pipe(
        map((actions: any[]) => this.snapShotData(actions)) // Process snapshot data
      );
    } catch (e) {
      console.error('Error fetching syndicate product:', e); // Log errors for debugging
      return of(null); // Return observable with null on error
    }
  }

  // Get the data from the current snapshot
  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): Data[] {
    try {
      return actions.map((a) => {
        const data = (a?.payload.doc?.data() as Data) ?? {};
        const id = a?.payload.doc.id;
        return { id, ...data };
      });
    } catch (e) {
      return e;
    }
  }

  // Get syndicate card height for styling purpose
  getSyndicateCardHeight(productDetails: Data) {
    let productLength = 0;
    try {
      productLength += productDetails?.products.map((draw) => draw?.draw_day?.length);
      if (productDetails?.cardType?.toLowerCase() === "private") {
        /* All measures are in vh */
        const defaultLength = 8;

        if (!productDetails?.products) {
          return defaultLength;
        }

        let requiredHeight = productDetails?.products?.length ? productLength * defaultLength + defaultLength : 0;
        return requiredHeight;
      }
    } catch (e) {
      return e;
    }
    if (productDetails?.syndicate_type_card === "MANAGED") {
      /* All measures are in vh */
      const defaultLength = 8;
      if (!productDetails?.product) {
        return defaultLength;
      }
      try {
        let requiredHeight = productDetails?.product?.length ? productLength * defaultLength + defaultLength : 0;

        /* Managed card doesn't require friend list, count and add players function */
        requiredHeight = requiredHeight - 8;

        return requiredHeight;
      } catch (e) {
        return e;
      }
    }
  }

  // Get product lines
  getPSLines(products) {
    let count = 0;
    if (products) {
      try {
        products?.map((product: Data) => {
          count += product?.total_lines * product?.draw_day?.length;
        });
      } catch (e) {
        return e;
      }
    }

    return count;
  }

  // This method is the get all products for the syndicate
  getProducts(syndicate) {
    let products: Data = [];
    if (!syndicate || !syndicate?.products || !syndicate?.lottery || !syndicate?.jackpot) return;
    try {
      syndicate.products.map((product) => {
        const lottery = syndicate?.lottery?.find(
          (x) => x?.real_name?.toLowerCase() === product?.product_name?.toLowerCase()
        );
        products.push({
          product_name: product?.product_name,
          draw_day_group: product?.draw_day,
          days: lottery?.draw_day,
          line_count: product?.total_lines,
          product_image: product?.image_url,
          lotteryInfo: lottery,
          jackpot: syndicate?.jackpot?.find(
            (x) => x?.real_name?.toLowerCase() === product?.product_name?.toLowerCase()
          ),
        });
      });
      return products;
    } catch (e) {
      return e;
    }
  }

  loadMoreSyndicates(event, cardLimit, syndicateCount, infiniteScroll, showText): Observable<Data> {
    const startIndex = cardLimit;
    const addNextTenItems = startIndex + 5;

    if (startIndex >= syndicateCount) {
      infiniteScroll.disabled = true;
      showText = true;
      setTimeout(() => {
        showText = false;
      }, 3000);
    } else {
      setTimeout(() => {
        cardLimit = addNextTenItems;
        this.syndicateDataService.updateSyndicateRequest(addNextTenItems);
        event?.target.complete();
      }, 2000);
    }

    return of({ cardLimit, syndicateCount, showText, infiniteScroll });
  }
}
